import { Injectable, NgZone } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { ITableService } from '@sp-shared/components/table';
import { HttpPermissionsService } from '@sp-core/http-services/http-permissions/http-permissions.service';
import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { catchError, map, tap, timeout } from 'rxjs/operators';
import { MetaHttpAdapter } from '@sp-core/http-adapters/meta-http-adapter';
import { RolesPermissionsAdapter } from '@sp-core/http-adapters/roles-permissions-adapter';
import { CookiesStorageService } from '@sp-core/services';
import { environment } from '@sp-environments/environment';

@Injectable({
  providedIn: 'root',
})
export class PermissionsService implements ITableService<IPermissions> {
  // Loading the permissions of a role is the last step between a logged-in user and the
  // application, and nothing aborts that request on its own: HttpClient has no default timeout
  // and the browser keeps waiting on a request the server accepted but never answered. Without
  // a deadline a stalled response is indistinguishable from "nothing happened" - the caller
  // cannot tell the user anything and we get no record of it. 15s is far above any real
  // response and below the edge timeout, so the frontend decides what the user sees, not nginx.
  private static readonly ROLE_SWITCH_TIMEOUT_MS = 15000;

  // AuthInterceptor answers a 401 with of(null), so an expired session reaches this service as
  // a successful empty response rather than as an error.
  private static readonly EMPTY_RESPONSE_ERROR = 'permissions.empty-response';

  private currentRole$: BehaviorSubject<IRole> = new BehaviorSubject<IRole>(null);
  private currentPermissions$: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor(
    private httpPermissionsService: HttpPermissionsService,
    private rolesPermissionsAdapter: RolesPermissionsAdapter,
    private cookiesStorage: CookiesStorageService,
    private ngZone: NgZone,
  ) {}

  public clearAuthData(): void {
    this.setAuthData(null, null);
  }

  public setAuthData(role: IRole, permission: any): void {
    this.currentRole$.next(role);
    this.currentPermissions$.next(permission);
    this.cookiesStorage.role = role;
  }

  public updateRole(role: IRole): Observable<IRole> {
    const startedAt = Date.now();

    return this.getPermissions(role.id).pipe(
      timeout(PermissionsService.ROLE_SWITCH_TIMEOUT_MS),
      tap((permission) => {
        this.setAuthData(role, permission);
      }),
      catchError((error) => {
        this.reportRoleSwitchFailure(role, error, Date.now() - startedAt);
        return throwError(() => error);
      }),
    );
  }

  public set role(role: IRole) {
    this.currentRole$.next(role);
  }

  public get role(): IRole {
    return this.currentRole$.getValue();
  }

  public get role$(): Observable<IRole> {
    return this.currentRole$.asObservable();
  }

  public get permissions(): Map<string, IPermissions> {
    return this.currentPermissions$.getValue();
  }

  public get permissions$(): Observable<Map<string, IPermissions>> {
    return this.currentPermissions$.asObservable();
  }

  public checkPermissions(data: string[]): boolean {
    // Permissions are cleared synchronously on logout while the current page is still rendered
    // until navigation destroys it; treat "no permissions" as "not allowed" instead of throwing.
    return data.some((permission) => this.permissions?.has(permission) ?? false);
  }

  public getTableData(params: HttpParamsOptions, apiParams: ApiParams): Observable<ITableData<IPermissions>> {
    return this.httpPermissionsService.getPermission(params, apiParams).pipe(
      map((data) => ({
        data: data.data.map((item) => this.rolesPermissionsAdapter.transformToPermissions(item)),
        meta: MetaHttpAdapter.transformToMeta(data.meta),
      })),
    );
  }

  public getPermissions(id: number): Observable<any> {
    return this.httpPermissionsService.getPermissions(id).pipe(
      map((response) => {
        // Without this guard the null that AuthInterceptor pushes down the stream after a 401
        // crashes on `.data` and surfaces as an unrelated TypeError instead of an auth failure.
        if (!response?.data) {
          throw new Error(PermissionsService.EMPTY_RESPONSE_ERROR);
        }

        const mapPermissions = new Map();
        response.data.forEach((permission) => {
          mapPermissions.set(permission?.key, permission);
        });

        return mapPermissions;
      }),
    );
  }

  // A failed role switch raises no exception anywhere: the interceptors either swallow it or
  // hand it to a subscriber with no error handler, so nothing reaches the ErrorHandler and
  // nothing reaches Sentry. Every caller goes through updateRole, so the record is written here
  // once rather than in each of the four call sites. Level is warning on purpose: a stalled or
  // rejected role switch is an operational event, not a crash, and must not drown the alerts
  // that watch for real code failures.
  private reportRoleSwitchFailure(role: IRole, error: any, durationMs: number): void {
    const reason = PermissionsService.resolveFailureReason(error);
    const context = {
      roleKey: role?.key,
      roleId: role?.id,
      durationMs,
      reason,
      status: error?.status ?? null,
      // The backend explains a 409 only in the message (the device cookie is missing or the device
      // is unknown). A timeout or an empty response has no server message, only our own error text.
      serverMessage: reason === 'http' ? error?.message ?? null : null,
    };

    // Sentry drops everything outside production (see beforeSend), so on localhost the console
    // is the only place this is visible - and it is the one detail that tells a stalled request
    // apart from a rejected one while testing.
    if (!environment.production) {
      console.warn('[RoleSwitch] failed', context, error);
    }

    this.ngZone.runOutsideAngular(() =>
      Sentry.captureMessage('Role switch failed', {
        level: 'warning',
        tags: { 'role_switch.status': String(context.status), 'role_switch.reason': reason },
        extra: context,
      }),
    );
  }

  private static resolveFailureReason(error: any): string {
    if (error?.name === 'TimeoutError') {
      return 'timeout';
    }

    if (error?.message === PermissionsService.EMPTY_RESPONSE_ERROR) {
      return 'empty-response';
    }

    return 'http';
  }
}
